'use client'

import * as React from 'react'
import { Search, Trash2, Clock, Loader2, Pencil, Check, X, Binoculars, Zap, CalendarDays, Lock, Mail, Bell, MapPin } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/components/auth'
import { api, ApiError } from '@/lib/api'
import { useSearchStore, useSettingsStore } from '@/lib/store'
import type { SavedSearch, RentalFilters, SortOption } from '@/lib/types'
import { FILTER_TAGS, STATIC_FILTER_TAGS } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { Switch } from '@/components/ui/switch'
import { useI18n } from '@/lib/i18n'
import { useSubscription } from '@/lib/subscription'
import { MAX_ROOSTRS_PENTHOUSE } from '@/lib/subscription-constants'
import { PenthouseBadge, UpgradePrompt as UpgradePromptDialog } from '@/components/subscription'
import { ROOSTR_COLORS, getRoostrDisplayName, getSearchSummary } from '@/lib/roostr-utils'

/** Extract a short location label from parsed_filters or fall back to the query. */
function getLocationLabel(search: SavedSearch): string | null {
  const f = search.parsed_filters
  if (f?.district) return f.district
  if (f?.city) return f.city
  if (search.original_query) {
    const q = search.original_query.trim()
    return q.length > 28 ? q.slice(0, 28) + '…' : q
  }
  return null
}

interface SearchesAndAgentsPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onLoadSearch?: (options?: { newSince?: string }) => void
}

export function SearchesAndAgentsPanel({ open, onOpenChange, onLoadSearch }: SearchesAndAgentsPanelProps) {
  const { user } = useAuth()
  const { setQuery, setFilters, setSort, setTravelTime, clearTags, addTag } = useSearchStore()
  const { notifications, setNotifications } = useSettingsStore()
  const { maxRoostrs: userMaxRoostrs, isPenthouse: isPenthouseUser, canUse } = useSubscription()
  const { t } = useI18n()

  const [searches, setSearches] = React.useState<SavedSearch[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [deletingId, setDeletingId] = React.useState<string | null>(null)
  const [togglingId, setTogglingId] = React.useState<string | null>(null)
  const [updatingCadenceId, setUpdatingCadenceId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [showUpgradePrompt, setShowUpgradePrompt] = React.useState(false)
  const hasFetchedOnce = React.useRef(false)

  // New listings counts
  const [newCounts, setNewCounts] = React.useState<Record<string, number>>({})

  // Inline rename state
  const [renamingId, setRenamingId] = React.useState<string | null>(null)
  const [renameValue, setRenameValue] = React.useState('')


  // Fetch saved searches when panel opens
  // First open: show spinner. Subsequent opens: show stale data, refresh silently.
  React.useEffect(() => {
    if (open && user) {
      fetchSavedSearches(!hasFetchedOnce.current)
    }
  }, [open, user])

  const fetchSavedSearches = async (showSpinner: boolean) => {
    if (!user) return

    if (showSpinner) {
      setIsLoading(true)
    }
    setError(null)

    try {
      const [savedSearches, counts] = await Promise.all([
        api.getSavedSearches(),
        api.getNotificationCountsBySearch().catch(() => ({} as Record<string, number>)),
      ])

      setSearches(savedSearches)
      setNewCounts(counts)
      hasFetchedOnce.current = true
    } catch (err) {
      console.error('Error fetching saved searches:', err)
      if (err instanceof ApiError && err.isUnauthorized) {
        setError(t('savedSearches.signInToView', 'Please sign in to view saved searches'))
      } else if (!hasFetchedOnce.current) {
        setError(t('savedSearches.failedToLoad', 'Failed to load saved searches'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const activeRoostrs = searches.filter(s => s.is_active)
  const availableSearches = searches.filter(s => !s.is_active)
  const totalFound = activeRoostrs.reduce((sum, s) => sum + (s.notification_count || 0), 0)

  const handleDelete = async (searchId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!user) return

    setDeletingId(searchId)
    try {
      await api.deleteSavedSearch(searchId)
      setSearches(prev => prev.filter(s => s.id !== searchId))
      window.dispatchEvent(new CustomEvent('saved-searches-changed'))
    } catch (err) {
      console.error('Error deleting search:', err)
      setError(t('savedSearches.failedToDelete', 'Failed to delete search'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleRoostr = async (searchId: string, currentActive: boolean, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!user) return

    // Check limit when activating
    if (!currentActive && activeRoostrs.length >= userMaxRoostrs) {
      if (isPenthouseUser) {
        setError(t('savedSearches.allDeployed', 'All 5 agents are deployed! Recall one to send another on a new mission.'))
      } else {
        setShowUpgradePrompt(true)
      }
      return
    }

    setTogglingId(searchId)
    setError(null)

    try {
      const newActive = !currentActive
      const updates: { is_active: boolean; roostr_name?: string; activity_blocks?: number[] } = { is_active: newActive }

      // Clear custom name and activity when recalling
      if (!newActive) {
        updates.roostr_name = ''
        updates.activity_blocks = [0, 0, 0, 0, 0, 0, 0]
      }

      const updated = await api.updateSavedSearch(searchId, updates)
      setSearches(prev => prev.map(s => s.id === searchId ? { ...s, ...updated } : s))
    } catch (err) {
      console.error('Error toggling roostr:', err)
      if (err instanceof ApiError) {
        setError(err.message || t('savedSearches.failedToUpdate', 'Failed to update notification'))
      } else {
        setError(t('savedSearches.failedToUpdate', 'Failed to update notification'))
      }
    } finally {
      setTogglingId(null)
    }
  }


  const handleStartRename = (searchId: string, currentName: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setRenamingId(searchId)
    setRenameValue(currentName || '')
  }

  const handleConfirmRename = async (searchId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!renameValue.trim()) return

    try {
      const updated = await api.updateSavedSearch(searchId, { roostr_name: renameValue.trim() })
      setSearches(prev => prev.map(s => s.id === searchId ? { ...s, ...updated } : s))
    } catch (err) {
      console.error('Error renaming roostr:', err)
    } finally {
      setRenamingId(null)
      setRenameValue('')
    }
  }

  const handleCancelRename = (e: React.MouseEvent) => {
    e.stopPropagation()
    setRenamingId(null)
    setRenameValue('')
  }

  const handleCadenceChange = async (searchId: string, cadence: 'daily' | 'instant', e: React.MouseEvent) => {
    e.stopPropagation()
    setUpdatingCadenceId(searchId)
    try {
      const updated = await api.updateSavedSearch(searchId, { cadence })
      setSearches(prev => prev.map(s => s.id === searchId ? { ...s, ...updated } : s))
    } catch {
      setError(t('savedSearches.failedToUpdate', 'Failed to update notification'))
    } finally {
      setUpdatingCadenceId(null)
    }
  }

  const handleLoadSearch = (search: SavedSearch, options?: { newSince?: string }) => {
    // Mark as seen (fire-and-forget to not block UI)
    api.markSearchSeen(search.id).catch(() => {})
    setNewCounts(prev => ({ ...prev, [search.id]: 0 }))

    setQuery(search.original_query || '')
    setFilters(search.parsed_filters as RentalFilters || { country: 'CZ' } as RentalFilters)
    setSort('newest')
    setTravelTime(search.travel_time || null)
    clearTags()

    // Create a set of static filter tag IDs for quick lookup
    const staticTagIds = new Set(STATIC_FILTER_TAGS.map(t => t.id))

    if (search.tags && search.tags.length > 0) {
      search.tags.forEach(tagId => {
        if (!staticTagIds.has(tagId)) {
          addTag(tagId)
        }
      })
    } else if (search.parsed_filters) {
      FILTER_TAGS.forEach((tag) => {
        if (staticTagIds.has(tag.id)) {
          return
        }
        const filterValue = search.parsed_filters?.[tag.value.field]
        if (filterValue !== undefined && filterValue === tag.value.value) {
          addTag(tag.id)
        }
      })
    }

    onOpenChange(false)
    onLoadSearch?.(options)
  }

  const { uiLanguage } = useI18n()

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const locale = uiLanguage === 'cs' ? 'cs-CZ' : 'en-GB'
    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    })
  }


  // Build slot index map: for each active roostr, what slot is it in?
  const roostrSlotMap = new Map<string, number>()
  activeRoostrs.forEach((s, i) => { roostrSlotMap.set(s.id, i) })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden flex flex-col max-h-[90vh]">
        <DialogTitle className="sr-only">Searches &amp; Agents</DialogTitle>

        {/* A. Simplified Header */}
        <div className="bg-card pl-4 pr-12 sm:pl-6 sm:pr-14 pt-5 sm:pt-6 pb-4 sm:pb-5 border-b border-border/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-primary/15 border-2 border-primary/30 flex items-center justify-center flex-shrink-0">
              <Binoculars className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg sm:text-xl font-bold text-foreground tracking-tight">{t('savedSearches.title', 'Searches & Agents')}</h2>
                {!isLoading && !error && (
                  <Badge variant="secondary" className="text-xs px-2 py-0.5">
                    {activeRoostrs.length} of {userMaxRoostrs} {t('savedSearches.activeOf', 'active')}
                  </Badge>
                )}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{t('savedSearches.subtitle', 'Deploy personal agents to find your next apartment')}</p>
            </div>
          </div>
        </div>


        {/* B. Search List (scrollable) */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-primary mb-3" />
              <p className="text-sm text-muted-foreground">{t('savedSearches.loading', 'Loading your searches...')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-16 px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
                <X className="h-8 w-8 text-destructive" />
              </div>
              <p className="text-destructive mb-4 font-medium">{error}</p>
              <Button variant="outline" size="lg" onClick={() => { setError(null); fetchSavedSearches(true) }}>
                {t('savedSearches.tryAgain', 'Try Again')}
              </Button>
            </div>
          ) : searches.length === 0 ? (
            <div className="text-center py-14 px-8">
              <div className="flex items-center justify-center gap-2.5 mb-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-10 w-10 rounded-full border-2 border-dashed border-muted-foreground/15"
                  />
                ))}
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{t('savedSearches.emptyTitle', 'No saved searches yet')}</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                {t('savedSearches.emptyText', 'Save your search criteria to quickly access them later and deploy agents to monitor for new listings.')}
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {searches.map((search, index) => {
                const slotIndex = roostrSlotMap.get(search.id)
                const isActive = search.is_active && slotIndex != null
                const newCount = newCounts[search.id] || 0
                const locationLabel = getLocationLabel(search)
                const cadence = search.cadence ?? 'daily'

                return (
                  <div
                    key={search.id}
                    className={cn(
                      'rounded-xl border bg-card transition-shadow hover:shadow-sm',
                      isActive ? 'border-border' : 'border-border/50',
                    )}
                    style={{ animation: `fadeInUp 0.3s ease-out ${index * 60}ms forwards` }}
                  >
                    {/* Card body */}
                    <div className="p-5">

                      {/* Row 1: Title + action buttons */}
                      <div className="flex items-start gap-3 mb-1">
                        <div className="flex-1 min-w-0">
                          {renamingId === search.id ? (
                            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                              <Input
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                className="h-9 text-base font-bold"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleConfirmRename(search.id, e as any)
                                  if (e.key === 'Escape') handleCancelRename(e as any)
                                }}
                              />
                              <button
                                onClick={(e) => handleConfirmRename(search.id, e)}
                                className="h-9 w-9 rounded-lg flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex-shrink-0"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelRename}
                                className="h-9 w-9 rounded-lg flex items-center justify-center border border-border text-muted-foreground hover:bg-muted transition-colors flex-shrink-0"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <h3
                              className="font-bold text-base text-foreground leading-snug cursor-pointer hover:text-primary transition-colors"
                              onClick={() => handleLoadSearch(search)}
                            >
                              {search.name}
                            </h3>
                          )}
                        </div>

                        {/* Buttons — always visible */}
                        {renamingId !== search.id && (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {newCount > 0 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleLoadSearch(search, { newSince: search.last_executed_at }) }}
                                className="h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors flex items-center gap-1.5"
                              >
                                {newCount} {t('savedSearches.new', 'New')}
                              </button>
                            )}
                            <button
                              onClick={(e) => handleStartRename(search.id, search.name, e)}
                              className="h-9 w-9 rounded-lg bg-foreground text-background hover:bg-foreground/80 flex items-center justify-center transition-colors flex-shrink-0"
                              title={t('savedSearches.rename', 'Rename')}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => handleDelete(search.id, e)}
                              disabled={deletingId === search.id}
                              className="h-9 w-9 rounded-lg border border-border text-muted-foreground hover:border-destructive/50 hover:text-destructive hover:bg-destructive/5 flex items-center justify-center transition-colors flex-shrink-0"
                              title={t('savedSearches.delete', 'Delete')}
                            >
                              {deletingId === search.id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Trash2 className="h-4 w-4" />
                              }
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Row 2: Created date */}
                      <p className="text-sm text-muted-foreground mb-4">
                        {t('savedSearches.created', 'Created')} {formatDate(search.created_at)}
                      </p>

                      {/* Row 3: Metadata chips */}
                      <div className="flex flex-wrap gap-2">
                        {locationLabel && (
                          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground bg-muted/60 rounded-lg px-3 py-1.5">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            {locationLabel}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground bg-muted/60 rounded-lg px-3 py-1.5">
                          <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                          {t('savedSearches.channelEmail', 'Email')}
                        </span>

                        {/* Cadence selector — only for active agents */}
                        {isActive && (
                          <div
                            className="inline-flex rounded-lg border border-border overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(['daily', 'instant'] as const).map((tier) => {
                              const isSelected = cadence === tier
                              const isInstant = tier === 'instant'
                              const isLocked = isInstant && !isPenthouseUser
                              const isUpdating = updatingCadenceId === search.id
                              return (
                                <button
                                  key={tier}
                                  disabled={isSelected || isUpdating}
                                  onClick={(e) => {
                                    if (isLocked) { e.stopPropagation(); setShowUpgradePrompt(true); return }
                                    handleCadenceChange(search.id, tier, e)
                                  }}
                                  title={isLocked ? t('savedSearches.instantLocked', 'Instant alerts require Penthouse') : undefined}
                                  className={cn(
                                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors',
                                    isSelected
                                      ? isInstant
                                        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                                        : 'bg-primary/10 text-primary'
                                      : isLocked
                                        ? 'text-muted-foreground/40 cursor-pointer hover:bg-muted/40'
                                        : 'text-muted-foreground hover:bg-muted/60 cursor-pointer',
                                  )}
                                >
                                  {isUpdating && isSelected
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : isLocked
                                      ? <Lock className="h-3.5 w-3.5" />
                                      : isInstant
                                        ? <Zap className="h-3.5 w-3.5" />
                                        : <CalendarDays className="h-3.5 w-3.5" />
                                  }
                                  {tier === 'daily'
                                    ? t('savedSearches.cadenceDaily', 'Daily')
                                    : t('savedSearches.cadenceInstant', 'Instant')
                                  }
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer: Show All + notification toggle */}
                    <div
                      className="flex items-center gap-3 px-5 py-3 border-t border-border/40"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="outline"
                        onClick={() => handleLoadSearch(search)}
                        className="h-9 px-4 text-sm font-medium rounded-lg"
                      >
                        {t('savedSearches.showAll', 'Show All')}
                      </Button>
                      <div className="flex-1" />
                      <span className={cn(
                        'text-sm font-medium transition-colors',
                        search.is_active ? 'text-foreground' : 'text-muted-foreground'
                      )}>
                        {search.is_active
                          ? t('savedSearches.stopNotifications', 'Turn off notifications')
                          : t('savedSearches.startNotifications', 'Turn on notifications')
                        }
                      </span>
                      {togglingId === search.id ? (
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      ) : (
                        <Switch
                          checked={search.is_active}
                          onCheckedChange={() => handleToggleRoostr(search.id, search.is_active, { stopPropagation: () => {} } as React.MouseEvent)}
                          disabled={togglingId === search.id}
                        />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>



        {/* Inline keyframes style */}
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </DialogContent>
      <UpgradePromptDialog
        open={showUpgradePrompt}
        onOpenChange={setShowUpgradePrompt}
      />
    </Dialog>
  )
}

// Compat alias
export { SearchesAndAgentsPanel as SavedSearchesPanel }
export { SearchesAndAgentsPanel as SearchesAndAlertsPanel }
